import React from 'react'
import flatMap from 'lodash/flatMap'
import values from 'lodash/values'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import maxBy from 'lodash/maxBy'
import isNil from 'lodash/isNil'

import { ItemProductCart } from './ProductSummary'

const PRODUCT_NAME = 'ProductNameContainer'
const PRODUCT_CONTENT = 'ProductContent'
const PRODUCT_IMAGE = 'ProductImageContainer'
const ORIGIN = 'OriginContainer'
const PRODUCT_OPTIONS = 'ProductOptions'
const PRODUCT_SERVICE_OPTIONS = 'ProductServiceOptions'
const PRODUCT_FOOTER = 'ProductFooter'
const PROMOTIONAL_TYPE = 'promotional'

const getQuantityInPromotionalItem = (item: ItemProductCart) => {
  const { prices } = item
  const originalPrice = maxBy(prices, 'price')
  return originalPrice?.quantity
}

export const getActualQuantity = (item: ItemProductCart) => {
  if (item.type !== PROMOTIONAL_TYPE) {
    return item.quantity
  }
  return getQuantityInPromotionalItem(item)
}

/**
 *
 * @description Receives a list of `ReactNode`s and distributes them among three properties in an object:
 * `content`, `options` and `custom` depending on the `ReactNode`'s component name
 * @param children a list of `ReactNode`s
 * @returns an object that has three properties: `content`, `options` and `custom`
 * @example
 *
 * const childrenByType = getChildrenByType([
 *  <ProductContent />,
 *  <ProductOptions />,
 *  <SomeCustomComponent />
 * ])
 *
 *
 */
export const getChildrenByType = (children: React.ReactNode) => {
  const childrenByType: Record<string, any> = {
    name: [],
    image: [],
    content: [],
    options: [],
    serviceOptions: [],
    custom: [],
    footer: [],
    origin: [],
  }
  React.Children.forEach(children, (child: any) => {
    if (isNil(child)) {
      return
    }
    const {
      type: { name, displayName },
    } = child
    const childType = displayName || name
    switch (childType) {
      case PRODUCT_NAME:
        childrenByType.name.push(child)
        break
      case PRODUCT_CONTENT:
        childrenByType.content.push(child)
        break
      case PRODUCT_OPTIONS:
        childrenByType.options.push(child)
        break
      case PRODUCT_SERVICE_OPTIONS:
        childrenByType.serviceOptions.push(child)
        break
      case PRODUCT_FOOTER:
        childrenByType.footer.push(child)
        break
      case PRODUCT_IMAGE:
        childrenByType.image.push(child)
        break
      case ORIGIN:
        childrenByType.origin.push(child)
        break
      default:
        childrenByType.custom.push(child)
        break
    }
  })
  return childrenByType
}

const getItemType = (item: Item, similitarItemsQuantity: number) => {
  if (item.isGift || similitarItemsQuantity > 1) {
    return 'promotional'
  }

  return 'default'
}
/**
 *  Responsible to map the entities Item[] to ItemProductCart[]
 *
 * @param items to be mapped
 */
export function mapItemsToCartItems(items: Item[]): ItemProductCart[] {
  const groupedItems = groupBy(items, 'id')
  const cartItems = flatMap(values(groupedItems), mapItemsToCartItem)
  return cartItems.reverse()
}

const mapItemsToCartItem = (items: Item[]): ItemProductCart[] => {
  const similitarItemsQuantity = items.length
  const itemsValue = sortBy(items, 'sellingPrice')
  const itemValue = itemsValue[0]
  const prices = itemsValue.map((item) => ({
    quantity: item.quantity,
    price: item.sellingPrice ?? (item.manualPrice || 0),
  }))
  const totalQuantity = prices.reduce((acc, item) => acc + item.quantity, 0)
  const item = {
    ...itemValue,
    quantity: totalQuantity,
    prices: prices,
    type: getItemType(itemValue, similitarItemsQuantity),
  }
  return [item]
}

export function __test__() {
  return {
    getQuantityInPromotionalItem,
    getItemType,
  }
}
